// The auth cards sit on a clear glass pane (see `#content` in webflow.scss). Every
// control inside them ships colours drawn for a white ground, so each card re-grounds
// them once at its own root rather than in the shared UUI theme — UUICheckbox is used
// across budgets, addresses and team-members too.
//
// Inputs are NOT re-grounded here: they carry `uiSkin="auth"`, which owns their rest,
// hover and focus states in the UUI theme. Overriding them from the card root as well
// would put the focus ring in two places at once.

// Checkbox: the box itself, not its label. Checked sinks into the card - deep iris box,
// lilac border to hold it against the glass, white tick. The tick is an SVG path the theme
// paints with `fill`, so `text-*` on the box never reaches it.
export const GLASS_FIELDS = [
  'tw:[&_button[role=checkbox]]:!border-white/60',
  'tw:[&_button[role=checkbox]]:!bg-white/10',
  'tw:[&_button[role=checkbox][data-state=checked]]:!bg-auth-btn-ink',
  'tw:[&_button[role=checkbox][data-state=checked]]:!border-auth-link',
  'tw:[&_button[role=checkbox][data-state=checked]_path.UUICheckboxCheckIndicator-path]:!fill-white',
  // the theme focuses in product blue; the ring replaces its shadow so the two cannot stack
  'tw:[&_button[role=checkbox]]:focus-visible:!shadow-none',
  'tw:[&_button[role=checkbox]]:focus-visible:!ring-2',
  'tw:[&_button[role=checkbox]]:focus-visible:!ring-auth-icon'
].join(' ')

// The card re-grounds every checkbox border above, so the theme's red never lands on an
// invalid box. Applied by the wrapper that knows about the error, since the box carries no
// invalid attribute of its own to hook onto. `[data-state]` matches every Radix box and is
// there only to outweigh the card-root border rule, which is otherwise equally specific.
export const GLASS_CHECKBOX_INVALID = 'tw:[&_button[role=checkbox][data-state]]:!border-auth-error'

// Tooltip on the auth cards. The shared bubble is white with navy type - drawn for the
// white product pages, and on the glass card it lands as a lit rectangle floating over
// the form. This restyles it through the component's own `contentClassName` rather than
// adding a skin to the shared tooltip theme, which no other surface needs yet. The last
// rule repaints the Radix arrow, which the theme fills separately.
export const AUTH_TOOLTIP = [
  'tw:!bg-[rgba(18,15,42,0.94)] tw:!text-white',
  'tw:!ring-1 tw:!ring-auth-link/55',
  'tw:!p-[16px_20px] tw:!text-[13px] tw:!leading-[19px]',
  'tw:[filter:drop-shadow(0_18px_40px_rgba(0,0,0,0.6))]',
  // the native arrow is filled but never stroked, so it broke the hairline it sat on and
  // pointed away from the field once the bubble shifted; the squared corner is the tail
  'tw:[&_svg]:!hidden',
  // start-aligned bubbles land flush with the field's outline; the shift is the field's own
  // padding, so the hint starts where what you type starts rather than at the pill edge
  'tw:[&[data-align=start]]:translate-x-[16px]'
].join(' ')

// The field hints carry nothing to click, so the bubble must not take the pointer: it opens
// above the field and covers the one before it, and on WebKit and Gecko that bubble swallowed
// the click meant for the covered field. Only the focus-driven hints get this - IconTooltip
// opens on hover, which needs the pointer to reach the bubble.
// The platform header is fixed to the top of the page and the bubble opens upward, but the
// positioning library only avoids the viewport edges -- so on the first field the hint opened
// straight over the header. Reserving the header's height makes it flip below instead.
export const AUTH_TOOLTIP_COLLISION = { top: 110 }

export const AUTH_TOOLTIP_HINT = `${AUTH_TOOLTIP} tw:!pointer-events-none`

// Select on the auth cards. Same story as the tooltip: the shared theme paints a white
// trigger with an aquamarine chevron and a white options panel, all drawn for the light
// pages. The trigger is matched to `uiSkin="auth"` inputs so the two controls cannot be
// told apart, and the panel becomes a darker sibling of the card - it is a portal, so it
// carries its own ground rather than inheriting the pane.
// `className` lands on the outer wrapper, but the visible pill is the combobox button
// inside it - the theme paints that one aquamarine. Everything below therefore reaches
// through to the button and to the value span, which carries its own colour too.
export const AUTH_SELECT_TRIGGER = [
  'tw:[&_button[role=combobox]]:!bg-[rgba(8,6,22,0.30)]',
  'tw:[&_button[role=combobox]]:!border tw:[&_button[role=combobox]]:!border-white/60',
  'tw:[&_button[role=combobox]]:!text-white',
  'tw:[&_button[role=combobox]]:hover:!border-white/85',
  'tw:[&_button[role=combobox]]:focus-visible:!outline-none',
  'tw:[&_button[role=combobox]]:focus-visible:!ring-2 tw:[&_button[role=combobox]]:focus-visible:!ring-auth-icon',
  'tw:[&_button[role=combobox]]:focus-visible:!border-auth-icon',
  'tw:[&_[data-uuiselect-trigger-value]]:!text-white tw:[&_[data-uuiselect-trigger-value]_span]:!text-white',
  'tw:[&_[data-uuiselect-trigger-icon]]:!text-auth-icon'
].join(' ')

export const AUTH_SELECT_OPTIONS = [
  'tw:!bg-[rgba(18,15,42,0.96)] tw:!ring-1 tw:!ring-white/22 tw:!text-white',
  // options: white type, a lifted row on keyboard/pointer highlight, accent on the picked one
  'tw:[&_[data-uuiselect-option]]:!text-white',
  'tw:[&_[data-uuiselect-option][data-highlighted]]:!bg-white/12',
  'tw:[&_[data-uuiselect-option][data-state=checked]]:!bg-auth-link tw:[&_[data-uuiselect-option][data-state=checked]]:!text-auth-accent-ink',
  // the filter row sits on the same dark ground, with its own divider
  'tw:[&_[data-uuiselect-filter]]:!border-white/20',
  'tw:[&_[data-uuiselect-filter-input]]:!bg-transparent tw:[&_[data-uuiselect-filter-input]]:!text-white',
  'tw:[&_[data-uuiselect-filter-input]]:placeholder:!text-white/60',
  'tw:[&_[data-uuiselect-filter-icon]]:!text-auth-icon'
].join(' ')

// Pill buttons carry the marketing site's button voice - Montserrat regular, upper-cased -
// so the app and the site read as one product. The label sits in its own span, which the
// card's blanket font rule reaches directly, so the family is re-pointed there as well.
// Text-variant buttons (promo code, timezone) are links in disguise and stay out of this.
// `whitespace-nowrap`: the pair of actions shares one row, and on a phone the shorter label
// was the one that wrapped, so the two buttons ended up different heights.
const GLASS_BTN_TYPE =
  'tw:!font-montserrat tw:!font-normal tw:!uppercase tw:whitespace-nowrap tw:[&_span]:!font-montserrat'

// Primary action inverts: white pill, deep iris ink. Navy (primary-text) belongs to the
// white product UI and read as a stray blue on the card.
export const GLASS_BTN_PRIMARY = `tw:!bg-white tw:!text-auth-btn-ink tw:hover:!bg-[#efeaff] ${GLASS_BTN_TYPE}`

// Secondary drops to a ghost so it does not compete with the inverted primary.
export const GLASS_BTN_GHOST = `tw:!bg-white/15 tw:!text-white tw:!ring-1 tw:!ring-white/35 tw:hover:!bg-white/25 ${GLASS_BTN_TYPE}`
